import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, getAuthToken } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/constants';
import {
  EyeIcon,
  PencilIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

interface Stats {
  published_posts: number;
  draft_posts: number;
  total_posts: number;
  total_views: number;
  approved_comments: number;
  pending_comments: number;
  subscribers: number;
}

interface RecentPost {
  id: string;
  title: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface RecentComment {
  id: string;
  author_name: string;
  content: string;
  created_at: string;
  post_title: string;
}

interface DashboardData {
  stats: Stats;
  recentPosts: RecentPost[];
  recentComments: RecentComment[];
  monthlyStats: Array<{ month: string; count: number }>;
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchDashboardData();
  }, [isAuthenticated, navigate]);

  const fetchDashboardData = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_ENDPOINTS.posts}/admin/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch dashboard data');

      const result = await response.json();
      if (result.success) {
        setData(result.data);
      }
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({
    title,
    value,
    icon: Icon,
    color,
    subtitle,
  }: {
    title: string;
    value: number | string;
    icon: any;
    color: string;
    subtitle?: string;
  }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const { stats, recentPosts, recentComments } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-display font-semibold text-gray-900">
          Dashboard
        </h2>
        <button
          onClick={fetchDashboardData}
          className="text-sm bg-ocean-500 text-white px-4 py-2 rounded-lg hover:bg-ocean-600 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Published Posts"
          value={stats.published_posts}
          icon={DocumentTextIcon}
          color="bg-green-500"
          subtitle={`${stats.draft_posts} drafts`}
        />
        <StatCard
          title="Total Views"
          value={stats.total_views?.toLocaleString() || '0'}
          icon={EyeIcon}
          color="bg-blue-500"
        />
        <StatCard
          title="Comments"
          value={stats.approved_comments}
          icon={ChatBubbleLeftRightIcon}
          color="bg-purple-500"
          subtitle={`${stats.pending_comments} pending`}
        />
        <StatCard
          title="Subscribers"
          value={stats.subscribers || '0'}
          icon={UserGroupIcon}
          color="bg-ocean-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Posts */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <PencilIcon className="h-5 w-5 mr-2 text-gray-400" />
              Recent Posts
            </h3>
          </div>
          <div className="p-6">
            {recentPosts.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No posts yet</p>
            ) : (
              <div className="space-y-4">
                {recentPosts.map(post => (
                  <div
                    key={post.id}
                    className="flex items-center justify-between py-2"
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 truncate">
                        {post.title}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {format(new Date(post.updated_at), 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        post.status === 'published'
                          ? 'bg-green-100 text-green-800'
                          : post.status === 'draft'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {post.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Comments */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-gray-400" />
              Pending Comments
            </h3>
          </div>
          <div className="p-6">
            {recentComments.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No pending comments
              </p>
            ) : (
              <div className="space-y-4">
                {recentComments.map(comment => (
                  <div
                    key={comment.id}
                    className="border-l-4 border-yellow-400 pl-4"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900">
                        {comment.author_name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {format(new Date(comment.created_at), 'MMM dd')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1 line-clamp-2">
                      {comment.content}
                    </p>
                    <p className="text-xs text-gray-500">
                      on "{comment.post_title}"
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/admin/posts/new')}
            className="flex items-center justify-center p-4 bg-ocean-50 border border-ocean-200 rounded-lg hover:bg-ocean-100 transition-colors group"
          >
            <PencilIcon className="h-5 w-5 text-ocean-600 mr-2" />
            <span className="font-medium text-ocean-700">Create New Post</span>
          </button>
          <button
            onClick={() => navigate('/admin/posts')}
            className="flex items-center justify-center p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors group"
          >
            <DocumentTextIcon className="h-5 w-5 text-gray-600 mr-2" />
            <span className="font-medium text-gray-700">Manage Posts</span>
          </button>
          <button
            onClick={() => navigate('/admin/comments')}
            className="flex items-center justify-center p-4 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors group"
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-purple-600 mr-2" />
            <span className="font-medium text-purple-700">Review Comments</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
